"use client";

import type { DashboardWidgetType } from "@/lib/api/types";

import { useDashboardTelemetryBatchOptional } from "@/components/dashboard/DashboardTelemetryBatchProvider";

import type { UseWidgetDataParams, UseWidgetDataResult } from "./useWidgetData";
import { useWidgetData } from "./useWidgetData";

const BATCH_TYPES = new Set<DashboardWidgetType>(["line_chart", "gauge", "stat"]);

/**
 * Uses dashboard batch telemetry when available; otherwise falls back to {@link useWidgetData}.
 */
export function useWidgetAggregateSeries(
  widgetId: string,
  widgetType: DashboardWidgetType,
  params: UseWidgetDataParams,
): UseWidgetDataResult {
  const batch = useDashboardTelemetryBatchOptional();
  const inBatch = Boolean(
    batch &&
      !params.customRange &&
      !params.preferSingleFetch &&
      BATCH_TYPES.has(widgetType) &&
      batch.batchAggregateWidgetIds.has(widgetId),
  );

  const single = useWidgetData({
    ...params,
    suspendFetch: inBatch,
  });

  if (!inBatch || !batch) {
    return single;
  }
  if (batch.globalQueryError) {
    return {
      data: null,
      isLoading: false,
      isFetching: batch.isFetching,
      error: batch.globalQueryError,
      refetch: batch.refetch,
      dataUpdatedAt: batch.dataUpdatedAt,
    };
  }
  const row = batch.seriesByWidgetId.get(widgetId);
  if (row?.error) {
    return {
      data: null,
      isLoading: false,
      isFetching: batch.isFetching,
      error: row.error,
      refetch: batch.refetch,
      dataUpdatedAt: batch.dataUpdatedAt,
    };
  }
  return {
    data: row?.data ?? null,
    isLoading: batch.isLoading && !row?.data,
    isFetching: batch.isFetching,
    error: null,
    refetch: batch.refetch,
    dataUpdatedAt: batch.dataUpdatedAt,
  };
}
